import {colors} from "@c10h/colors";
import {useTranslation} from "ni18n";
import React from "react";
import {useSwipeable} from "react-swipeable";
import {LazyImg} from "src/components/_common/LazyImg";
import {Bio} from "src/constants/BiosConstants";
import {Lookup} from "src/types";
import {useKeyHandler} from "src/useEscape";
import useMediaQuery from "src/useMediaQuery";
import ImageFlipper from "../ImageFlipper";
import {Btn} from "../V5Comp";

type Props = {
  person: Bio;
  activeIndex: number;
  total: number;
  setActiveProfile: Lookup<() => void>;
  closeRef?: React.MutableRefObject<HTMLDivElement>;
  lastItemRef?: React.MutableRefObject<HTMLSpanElement>;
  closingFocusRef: React.MutableRefObject<HTMLDivElement>;
};

const Profile: React.FC<Props> = ({
  activeIndex,
  setActiveProfile,
  total,
  closeRef,
  lastItemRef,
  closingFocusRef,
  person: {title, name, bio, linkedIn, twitter, interests, images},
}) => {
  const i18n = useTranslation();

  const swipeHandlers = useSwipeable({
    onSwipedLeft: setActiveProfile.next,
    onSwipedRight: setActiveProfile.prev,
  });

  useKeyHandler("ArrowRight", setActiveProfile.next);
  useKeyHandler("ArrowLeft", setActiveProfile.prev);
  useKeyHandler("Escape", () => {
    setActiveProfile.none();
    if (closingFocusRef.current) {
      closingFocusRef.current.focus();
    }
  });

  const buttonDimensions = {
    height: 20,
    width: 20,
  };

  const buttonClasses =
    "br50 gray800 white-sm p5 bg-white bg-gray800-sm ml3 cp df aic jcc fs26 focus-bsDarkBlue3";

  const isSm = useMediaQuery("sm");

  const keyDownHandler = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      setActiveProfile.next();
    }
    // @ts-expect-error TS2532: Object is possibly 'undefined'.
    if (!e.shiftKey && e.key === "Tab" && closeRef.current) {
      // @ts-expect-error TS2532: Object is possibly 'undefined'.
      closeRef.current.focus();
    }
  };

  return (
    <div className="df fdc-sm ph8 ph2-md pv6 ph0-sm hide-scroll-bars maxw20-sm">
      {isSm ? (
        <div className="df pos-r w140 ovf-x-a ma mb6 pl30" style={{right: 120}}>
          {images.map((item, i) => (
            <LazyImg
              key={`${i}-${item.src}`}
              src={`/static/img/v5/${item.src}.jpg`}
              aria-label={item.alt}
              alt={item.alt}
              className="br3 obfc oh h48 minw16 mr3"
            />
          ))}
          <div className="minw4 minh10" />
        </div>
      ) : (
        <div className="pr14">
          <ImageFlipper imageList={images} />
        </div>
      )}
      <div className="tal w100 w80-md pr2-sm" {...swipeHandlers}>
        <span className="font-mr ttu lh13 fs12">{title}</span>

        <h4
          className={`font-csb fs48 fs${
            name.length > 17 ? "24-sm lh14" : "32-sm"
          } mb3 fs40-md lh1 mt2`}
        >
          {name}
        </h4>
        <div className="mb8 mb3-md df aic fw">
          {linkedIn && (
            <Btn
              className={`focus-darkBlue3 p3-f focus-bsDarkBlue3 mr1 mb1 event-n9JmcB-${name}`}
              bg={colors.gray100}
              hoverBg={colors.gray50}
              color={colors.gray800}
              fontSize={0.75}
              href={linkedIn}
              aria-label={i18n.t("Go to the LinkedIn page of {{name}}", {name})}
            >
              <span className="cIcon-linkedin fs16" />
            </Btn>
          )}
          {twitter && (
            <Btn
              className={`focus-darkBlue3 p3-f focus-bsDarkBlue3 event-MAD8mt-${name}`}
              bg={colors.gray100}
              hoverBg={colors.gray50}
              color={colors.gray800}
              fontSize={0.75}
              href={`https://twitter.com/${twitter.replace("@", "")}`}
              aria-label={i18n.t("Go to the Twitter feed of {{name}}", {name})}
            >
              <span className="cIcon-twitter fs16" />
            </Btn>
          )}
        </div>
        <div className="ovf-y-a mb10" style={{maxHeight: "40vh"}}>
          <p className="fs14 font-ir m0 p0">{bio}</p>
          <div className="minh10" />
        </div>
        {interests && (
          <div className="dn-sm">
            <span className="db font-mr ttu fs12 mb2">{i18n.t("Interests")}</span>
            <span className="font-ib fs12">{interests.join("  /  ")}</span>
          </div>
        )}
      </div>

      <div
        className="pos-a m4-sm right0 white gray800-sm df aic jcfe"
        style={{bottom: isSm ? 50 : -55}}
      >
        <span className="font-mb fs12">
          {i18n.t("{{number}} of {{total}}", {number: activeIndex + 1, total})}
        </span>
        <span
          role="button"
          className={buttonClasses}
          aria-label={i18n.t("Go to previous profile")}
          style={buttonDimensions}
          tabIndex={0}
          onClick={setActiveProfile.prev}
          onKeyDown={e => e.key === "Enter" && setActiveProfile.prev()}
        >
          <span className="cIcon-arrow-left" />
        </span>
        <span
          role="button"
          ref={lastItemRef}
          className={buttonClasses}
          aria-label={i18n.t("Go to next profile")}
          style={buttonDimensions}
          tabIndex={0}
          onClick={setActiveProfile.next}
          onKeyDown={keyDownHandler}
        >
          <span className="cIcon-arrow-right2" />
        </span>
      </div>
    </div>
  );
};

export default Profile;
